/*
 * Navigation disclosure for staff work awaiting a membership or content decision.
 * Used by AppNavigation with the shared inbox metadata and StaffWorkList.
 * Each grouping mode opens a fresh queue while the trigger retains keyboard focus behavior.
 * Example: registerReviewsMenu(registry);
 *          <ReviewsMenu context={context} source={inbox}/>
 */

import { UIElement, defineUIElements } from '../../runtime/components/ui-element.js';
import { Icon } from '../../runtime/components/presentation.jsx';
import { servicesContext } from '../../runtime/services.js';
import { registerStaffWork, StaffWorkList } from '../ui/staff-work-list.jsx';
import { readStaffWorkGrouped, writeStaffWorkGrouped } from '../ui/staff-work-list.js';
import { positionNavigationMenu } from './menu-position.js';

export class ReviewsMenu extends UIElement {
  static properties = {
    source: { attribute: false },
    snapshot: { state: true },
    open: { state: true },
    grouped: { state: true }
  };

  constructor() {
    super();
    this.open = this.grouped = false;
    this.captureContainer = (node) => (this.container = node);
    this.captureTrigger = (node) => (this.trigger = node);
    this.captureMenu = (node) => (this.menu = node);
    this.captureQueue = (node) => (this.queue = node);
    this.bindMethods('close');
    this.own(() => this.stopSource?.());
  }

  willUpdate(changed) {
    super.willUpdate(changed);
    servicesContext(this.context, ['auth', 'storage', 'a11y']);

    if (this.observedSource !== this.source) {
      this.stopSource?.();
      this.observedSource = this.source;
      this.snapshot = this.source.get();
      this.stopSource = this.source.subscribe(
        this.guard(() => {
          const next = this.source.get();
          if (this.open && this.snapshot.staffWork !== next.staffWork) this.refreshQueue = true;
          this.snapshot = next;
        })
      );
    }

    const identity = this.context.services.auth.getAuthContext();
    if (!this.account || this.identity !== identity) {
      this.identity = identity;
      this.account = this.context.services.storage.accountStorage();
      this.grouped = readStaffWorkGrouped(this.account);
      this.open = false;
    }
  }

  updated(changed) {
    super.updated(changed);
    const life = this.connection;
    if (!life || !this.menu) return;
    if (life !== this.boundConnection) {
      this.boundConnection = life;
      const outside = life.guard((event) => {
        if (!this.container.contains(event.target)) this.close();
      });
      const resize = life.guard(() => {
        if (this.open) positionNavigationMenu(this.trigger, this.menu, this.context.window);
      });
      this.context.document.addEventListener('click', outside);
      life.own(() => this.context.document.removeEventListener('click', outside));
      this.context.window.addEventListener('resize', resize);
      life.own(() => this.context.window.removeEventListener('resize', resize));
    }

    if (this.open) positionNavigationMenu(this.trigger, this.menu, this.context.window);
    if (this.refreshQueue && this.queue) {
      this.refreshQueue = false;
      void this.queue.refresh();
    }
  }

  configureQueue(grouped) {
    const source = this.source;
    const origin = this.context.services.auth.getAuthContext();
    this.onQueueMeta = (meta) => source.updateMeta(meta, origin);
    this.initialResponse = grouped ? null : source.get().staffWork;
    this.refreshQueue = false;
  }

  toggle() {
    if (!this.connection) return;
    if (this.open) return this.close();
    this.grouped = readStaffWorkGrouped(this.account);
    this.configureQueue(this.grouped);
    this.open = true;
  }

  close(restore = false) {
    this.open = false;
    if (restore) this.context.services.a11y.focusElement(this.trigger);
  }

  mode(grouped) {
    if (this.grouped === grouped) return;
    writeStaffWorkGrouped(grouped, this.account);
    this.configureQueue(grouped);
    this.grouped = grouped;
  }

  keydown(event) {
    if (
      event.key !== 'Escape' ||
      event.defaultPrevented ||
      event.isComposing ||
      this.context.services.a11y.hasOpenModal() ||
      event.ctrlKey ||
      event.metaKey ||
      event.altKey ||
      event.shiftKey ||
      !this.open
    )
      return;
    event.preventDefault();
    event.stopPropagation();
    this.close(true);
  }

  focusout(event) {
    if (event.relatedTarget && !this.container.contains(event.relatedTarget)) this.close();
  }

  render() {
    const capsule = this.snapshot?.capsule;
    if (!capsule) return null;
    const label = capsule.total > 0 ? `Reviews, ${capsule.total} items` : 'Reviews';
    return (
      <div ref={this.captureContainer} class="reviews-menu-container" hidden={!capsule.eligible} id="reviews-menu-container" onKeyDown={this.keydown} onFocusout={this.focusout}>
        <button ref={this.captureTrigger} id="reviews-menu-button" class="nav-icon-button reviews-nav-button" title={label} aria-label={label} aria-controls="reviews-menu" aria-expanded={String(this.open)} onClick={this.toggle}>
          <Icon name="clipboard-check" classes="icon-sm"/>
          <span id="reviews-count" class="reviews-nav-badge" hidden={capsule.total < 1}>
            {capsule.badge}
          </span>
        </button>
        <div ref={this.captureMenu} id="reviews-menu" class="reviews-menu-dropdown" hidden={!this.open}>
          {this.open ? (
            <>
              <div class="reviews-menu-header">
                <strong>Reviews</strong>
                <div class="reviews-group-toggle" role="group" aria-label="Review list layout">
                  <button
                    type="button"
                    aria-pressed={String(!this.grouped)}
                    onClick={() => this.mode(false)}>
                    Ungrouped
                  </button>
                  <button
                    type="button"
                    aria-pressed={String(this.grouped)}
                    onClick={() => this.mode(true)}>
                    Grouped
                  </button>
                </div>
              </div>
              <div class="reviews-menu-body">
                <StaffWorkList
                  key={this.grouped}
                  ref={this.captureQueue}
                  context={this.context}
                  grouped={this.grouped}
                  initialResponse={this.initialResponse}
                  onMeta={this.onQueueMeta}
                  active={this.active}/>
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }
}

export function registerReviewsMenu(registry) {
  registerStaffWork(registry);
  defineUIElements(registry, { 'tbc-reviews-menu': ReviewsMenu });
}
